import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import { 
  ClipboardCheck, Users, Calendar, Check, X, RefreshCw,
  ChevronLeft, ChevronRight, AlertCircle, CheckCircle, Clock,
  Download, UserCog, GraduationCap, CalendarDays, CalendarRange,
  LogIn, LogOut, BarChart3, PieChart as PieChartIcon, TrendingUp, 
  AlertTriangle, Coffee, Sun
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to check if a date is Sunday
const isSunday = (dateStr) => {
  const date = new Date(dateStr);
  return date.getDay() === 0;
};

// Helper function to format date for display
const formatDateDisplay = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const CHART_COLORS = ['#10b981', '#ef4444', '#f59e0b', '#6366f1', '#8b5cf6'];

const CenterAttendancePage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('student'); // 'student', 'staff', 'analytics'
  const [dateMode, setDateMode] = useState('daily'); // 'daily', 'monthly', 'manual'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [manualStartDate, setManualStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [manualEndDate, setManualEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [staffAttendance, setStaffAttendance] = useState({});
  const [staffTimeIn, setStaffTimeIn] = useState({});
  const [staffTimeOut, setStaffTimeOut] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsReportType, setAnalyticsReportType] = useState('staff'); // 'staff' or 'student'

  // Check if selected date is Sunday (Holiday)
  const isSelectedDateSunday = useMemo(() => isSunday(selectedDate), [selectedDate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate, selectedBatch, activeTab, dateMode, selectedMonth, manualStartDate, manualEndDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch batches
      const batchRes = await axios.get(`${API}/center/batches`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }));
      setBatches(batchRes.data || []);

      // Fetch students
      const studentsRes = await axios.get(`${API}/center/students`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }));
      const studentData = studentsRes.data || [];
      setStudents(studentData);

      // Fetch staff from API
      const staffRes = await axios.get(`${API}/center/staff`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }));
      const staffData = staffRes.data || [];
      setStaff(staffData);

      // Fetch student attendance from API based on dateMode
      await fetchStudentAttendance(studentData);

      // Fetch staff attendance from API
      await fetchStaffAttendance(staffData);
      
      // Fetch analytics data
      if (activeTab === 'analytics') {
        await fetchAnalyticsData();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentAttendance = async (studentData) => {
    try {
      let params = {};
      if (dateMode === 'daily') {
        params = { date: selectedDate };
      } else if (dateMode === 'monthly') {
        params = { month: selectedMonth };
      } else if (dateMode === 'manual') {
        params = { start_date: manualStartDate, end_date: manualEndDate };
      }

      const attendanceRes = await axios.get(`${API}/center/student-attendance`, {
        headers: getAuthHeaders(),
        params
      }).catch(() => ({ data: [] }));

      const attendanceRecords = attendanceRes.data || [];
      
      const initAttendance = {};
      
      // Default all students - if Sunday mark as holiday, otherwise absent
      studentData.forEach(s => {
        initAttendance[s.id] = isSelectedDateSunday ? 'holiday' : 'absent';
      });
      
      // Override with actual records from database
      attendanceRecords.forEach(record => {
        initAttendance[record.student_id] = record.status || 'absent';
      });
      
      setAttendance(initAttendance);
    } catch (error) {
      console.error('Error fetching student attendance:', error);
      // Default all to absent (or holiday if Sunday)
      const initAttendance = {};
      studentData.forEach(s => {
        initAttendance[s.id] = isSelectedDateSunday ? 'holiday' : 'absent';
      });
      setAttendance(initAttendance);
    }
  };

  const fetchStaffAttendance = async (staffData) => {
    try {
      let params = {};
      if (dateMode === 'daily') {
        params = { date: selectedDate };
      } else if (dateMode === 'monthly') {
        params = { month: selectedMonth };
      } else if (dateMode === 'manual') {
        params = { start_date: manualStartDate, end_date: manualEndDate };
      }

      const attendanceRes = await axios.get(`${API}/center/staff/attendance`, {
        headers: getAuthHeaders(),
        params
      }).catch(() => ({ data: [] }));

      const attendanceRecords = attendanceRes.data || [];
      
      const initStaffAttendance = {};
      const initTimeIn = {};
      const initTimeOut = {};
      
      // Default all staff - if Sunday mark as holiday
      staffData.forEach(s => {
        initStaffAttendance[s.id] = isSelectedDateSunday ? 'holiday' : 'absent';
        initTimeIn[s.id] = '09:00';
        initTimeOut[s.id] = '18:00';
      });
      
      // Override with actual records
      attendanceRecords.forEach(record => {
        initStaffAttendance[record.staff_id] = record.status || 'absent';
        initTimeIn[record.staff_id] = record.time_in || '09:00';
        initTimeOut[record.staff_id] = record.time_out || '18:00';
      });
      
      setStaffAttendance(initStaffAttendance);
      setStaffTimeIn(initTimeIn);
      setStaffTimeOut(initTimeOut);
    } catch (error) {
      console.error('Error fetching staff attendance:', error);
      const initStaffAttendance = {};
      const initTimeIn = {};
      const initTimeOut = {};
      staffData.forEach(s => {
        initStaffAttendance[s.id] = isSelectedDateSunday ? 'holiday' : 'absent';
        initTimeIn[s.id] = '09:00';
        initTimeOut[s.id] = '18:00';
      });
      setStaffAttendance(initStaffAttendance);
      setStaffTimeIn(initTimeIn);
      setStaffTimeOut(initTimeOut);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      // Get current month data
      const currentMonth = new Date().toISOString().slice(0, 7);
      
      // Fetch STAFF attendance analytics
      const staffRes = await axios.get(`${API}/center/staff/attendance`, {
        headers: getAuthHeaders(),
        params: { month: currentMonth }
      }).catch(() => ({ data: [] }));

      const staffRecords = staffRes.data || [];
      
      // Get all staff
      const staffList = await axios.get(`${API}/center/staff`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }));
      const allStaff = staffList.data || [];
      
      // Staff Monthly summary
      const staffSummary = allStaff.map(s => {
        const sRecords = staffRecords.filter(r => r.staff_id === s.id);
        const presentDays = sRecords.filter(r => r.status === 'present').length;
        const absentDays = sRecords.filter(r => r.status === 'absent').length;
        const lateDays = sRecords.filter(r => {
          if (r.time_in && r.status === 'present') {
            const [hours, minutes] = r.time_in.split(':').map(Number);
            return hours > 9 || (hours === 9 && minutes > 15);
          }
          return false;
        }).length;
        const leaveDays = sRecords.filter(r => r.status === 'leave' || r.status === 'half_day').length;
        
        return {
          name: s.name,
          type: 'staff',
          present: presentDays,
          absent: absentDays,
          late: lateDays,
          leave: leaveDays,
          total: sRecords.length
        };
      });

      // Fetch STUDENT attendance analytics
      const studentAnalyticsRes = await axios.get(`${API}/center/student-attendance/analytics`, {
        headers: getAuthHeaders(),
        params: { month: currentMonth }
      }).catch(() => ({ data: null }));

      const studentAnalytics = studentAnalyticsRes.data || null;
      
      // Student summary
      const studentSummary = studentAnalytics?.studentSummary || [];

      // Staff overall stats
      const staffTotalPresent = staffRecords.filter(r => r.status === 'present').length;
      const staffTotalAbsent = staffRecords.filter(r => r.status === 'absent').length;
      const staffTotalLate = staffRecords.filter(r => {
        if (r.time_in && r.status === 'present') {
          const [hours, minutes] = r.time_in.split(':').map(Number);
          return hours > 9 || (hours === 9 && minutes > 15);
        }
        return false;
      }).length;
      const staffTotalLeave = staffRecords.filter(r => r.status === 'leave' || r.status === 'half_day').length;

      // Staff Daily trend (last 7 days)
      const staffDailyTrend = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayRecords = staffRecords.filter(r => {
          const recordDate = new Date(r.attendance_date).toISOString().split('T')[0];
          return recordDate === dateStr;
        });
        
        staffDailyTrend.push({
          date: date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' }),
          present: dayRecords.filter(r => r.status === 'present').length,
          absent: dayRecords.filter(r => r.status === 'absent').length,
          isSunday: date.getDay() === 0
        });
      }

      setAnalyticsData({
        // Staff data
        staffSummary,
        staffOverallStats: {
          present: staffTotalPresent,
          absent: staffTotalAbsent,
          late: staffTotalLate,
          leave: staffTotalLeave
        },
        staffDailyTrend,
        staffPieData: [
          { name: 'Present', value: staffTotalPresent, color: '#10b981' },
          { name: 'Absent', value: staffTotalAbsent, color: '#ef4444' },
          { name: 'Late', value: staffTotalLate, color: '#f59e0b' },
          { name: 'Leave', value: staffTotalLeave, color: '#6366f1' }
        ],
        // Student data
        studentSummary,
        studentOverallStats: studentAnalytics?.overallStats || { present: 0, absent: 0, holiday: 0 },
        studentDailyTrend: studentAnalytics?.dailyTrend || [],
        studentPieData: studentAnalytics?.pieData || [],
        // Legacy (for backward compatibility)
        overallStats: {
          present: staffTotalPresent,
          absent: staffTotalAbsent,
          late: staffTotalLate,
          leave: staffTotalLeave
        },
        dailyTrend: staffDailyTrend,
        pieData: [
          { name: 'Present', value: staffTotalPresent, color: '#10b981' },
          { name: 'Absent', value: staffTotalAbsent, color: '#ef4444' },
          { name: 'Late', value: staffTotalLate, color: '#f59e0b' },
          { name: 'Leave', value: staffTotalLeave, color: '#6366f1' }
        ]
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsData(null);
    }
  };

  const toggleAttendance = (id, isStaff = false) => {
    // Don't allow changes on Sunday (holiday)
    if (isSelectedDateSunday) {
      toast.info('Sunday holiday hai, attendance mark nahi ho sakti');
      return;
    }

    if (isStaff) {
      setStaffAttendance(prev => ({
        ...prev,
        [id]: prev[id] === 'present' ? 'absent' : 'present'
      }));
    } else {
      setAttendance(prev => ({
        ...prev,
        [id]: prev[id] === 'present' ? 'absent' : 'present'
      }));
    }
  };

  const markAllPresent = (isStaff = false) => {
    if (isSelectedDateSunday) {
      toast.info('Sunday holiday hai, attendance mark nahi ho sakti');
      return;
    }

    if (isStaff) {
      const newAttendance = {};
      staff.forEach(s => {
        newAttendance[s.id] = 'present';
      });
      setStaffAttendance(prev => ({ ...prev, ...newAttendance }));
      toast.success('Sabhi staff present mark ho gaye');
    } else {
      const newAttendance = {};
      filteredStudents.forEach(s => {
        newAttendance[s.id] = 'present';
      });
      setAttendance(prev => ({ ...prev, ...newAttendance }));
      toast.success('Sabhi students present mark ho gaye');
    }
  };

  const saveAttendance = async () => {
    if (isSelectedDateSunday) {
      toast.info('Sunday holiday hai, attendance save nahi ho sakti');
      return;
    }

    setIsSaving(true);
    try {
      if (activeTab === 'student') {
        // Use new simple student attendance API
        await axios.post(`${API}/center/student-attendance`, {
          date: selectedDate,
          attendance: attendance
        }, { headers: getAuthHeaders() });
        toast.success('Student attendance save ho gayi');
      } else {
        const attendanceData = staff.map(s => ({
          staff_id: s.id,
          status: staffAttendance[s.id] || 'absent',
          time_in: staffTimeIn[s.id] || '09:00',
          time_out: staffTimeOut[s.id] || '18:00'
        }));

        await axios.post(`${API}/center/staff/attendance`, {
          date: selectedDate,
          attendance: attendanceData
        }, { headers: getAuthHeaders() });
        toast.success('Staff attendance save ho gayi');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Attendance save nahi ho payi. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Full Excel Download Function
  const downloadExcel = () => {
    const data = activeTab === 'student' ? filteredStudents : staff;
    const attendanceData = activeTab === 'student' ? attendance : staffAttendance;
    
    let excelData = [];
    let fileName = '';
    
    if (activeTab === 'student') {
      excelData = data.map((item, idx) => ({
        'Sr No': idx + 1,
        'Student Name': item.name || 'N/A',
        'Batch/Course': item.batch || item.course_name || 'N/A',
        'Phone': item.phone || item.mobile || 'N/A',
        'Status': attendanceData[item.id] === 'present' ? 'Present' : attendanceData[item.id] === 'holiday' ? 'Holiday (Sunday)' : 'Absent',
        'Date': selectedDate
      }));
      fileName = `Student_Attendance_${dateMode === 'daily' ? selectedDate : dateMode === 'monthly' ? selectedMonth : `${manualStartDate}_to_${manualEndDate}`}.xlsx`;
    } else {
      excelData = data.map((item, idx) => ({
        'Sr No': idx + 1,
        'Staff Name': item.name || 'N/A',
        'Role': item.role || 'N/A',
        'Department': item.department || 'N/A',
        'Phone': item.phone || 'N/A',
        'Status': staffAttendance[item.id] === 'present' ? 'Present' : staffAttendance[item.id] === 'holiday' ? 'Holiday (Sunday)' : 'Absent',
        'Time In': staffTimeIn[item.id] || '-',
        'Time Out': staffTimeOut[item.id] || '-',
        'Late Arrival': staffTimeIn[item.id] && staffAttendance[item.id] === 'present' ? 
          (parseInt(staffTimeIn[item.id].split(':')[0]) > 9 || 
           (parseInt(staffTimeIn[item.id].split(':')[0]) === 9 && parseInt(staffTimeIn[item.id].split(':')[1]) > 15) ? 'Yes' : 'No') : '-',
        'Date': selectedDate
      }));
      fileName = `Staff_Attendance_${dateMode === 'daily' ? selectedDate : dateMode === 'monthly' ? selectedMonth : `${manualStartDate}_to_${manualEndDate}`}.xlsx`;
    }
    
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeTab === 'student' ? 'Student Attendance' : 'Staff Attendance');
    
    const maxWidth = 20;
    const colWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.min(Math.max(key.length, ...excelData.map(row => String(row[key] || '').length)), maxWidth)
    }));
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, fileName);
    toast.success(`${activeTab === 'student' ? 'Student' : 'Staff'} attendance Excel me download ho gayi`);
  };

  const filteredStudents = selectedBatch === 'all' 
    ? students 
    : students.filter(s => s.batch === selectedBatch || s.course_name === selectedBatch);

  const currentData = activeTab === 'student' ? filteredStudents : staff;
  const currentAttendance = activeTab === 'student' ? attendance : staffAttendance;
  
  const presentCount = currentData.filter(s => currentAttendance[s.id] === 'present').length;
  const absentCount = currentData.filter(s => currentAttendance[s.id] === 'absent').length;
  const holidayCount = currentData.filter(s => currentAttendance[s.id] === 'holiday').length;
  const attendancePercent = currentData.length ? Math.round((presentCount / currentData.length) * 100) : 0;

  // Count late arrivals for staff
  const lateCount = activeTab === 'staff' ? staff.filter(s => {
    if (staffAttendance[s.id] === 'present' && staffTimeIn[s.id]) {
      const [hours, minutes] = staffTimeIn[s.id].split(':').map(Number);
      return hours > 9 || (hours === 9 && minutes > 15);
    }
    return false;
  }).length : 0;

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Analytics Tab Content
  const renderAnalytics = () => {
    const isStaffReport = analyticsReportType === 'staff';
    const currentStats = isStaffReport ? analyticsData?.staffOverallStats : analyticsData?.studentOverallStats;
    const currentSummary = isStaffReport ? analyticsData?.staffSummary : analyticsData?.studentSummary;
    const currentTrend = isStaffReport ? analyticsData?.staffDailyTrend : analyticsData?.studentDailyTrend;
    const currentPieData = isStaffReport ? analyticsData?.staffPieData : analyticsData?.studentPieData;
    
    return (
    <div className="space-y-6">
      {/* Report Type Toggle - Staff vs Student */}
      <div className="bg-white rounded-xl p-2 border border-slate-200 shadow-sm inline-flex gap-2">
        <button
          onClick={() => setAnalyticsReportType('staff')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            analyticsReportType === 'staff'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <UserCog className="w-5 h-5" />
          Staff Report
        </button>
        <button
          onClick={() => setAnalyticsReportType('student')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            analyticsReportType === 'student'
              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <GraduationCap className="w-5 h-5" />
          Student Report
        </button>
      </div>

      {/* Analytics Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-white/80 text-sm">Total Present</p>
              <p className="text-2xl font-bold">{currentStats?.present || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-8 h-8 opacity-80" />
            <div>
              <p className="text-white/80 text-sm">Total Absent</p>
              <p className="text-2xl font-bold">{currentStats?.absent || 0}</p>
            </div>
          </div>
        </div>
        {isStaffReport ? (
          <>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-white/80 text-sm">Late Arrivals</p>
                  <p className="text-2xl font-bold">{currentStats?.late || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center gap-3">
                <Coffee className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-white/80 text-sm">On Leave</p>
                  <p className="text-2xl font-bold">{currentStats?.leave || 0}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center gap-3">
                <Sun className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-white/80 text-sm">Holidays</p>
                  <p className="text-2xl font-bold">{currentStats?.holiday || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-white/80 text-sm">Total Students</p>
                  <p className="text-2xl font-bold">{currentSummary?.length || 0}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Bar Chart - Attendance Summary */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Monthly {isStaffReport ? 'Staff' : 'Student'} Attendance Summary
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={currentSummary || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Legend />
                <Bar dataKey="present" fill="#10b981" name="Present" radius={[4, 4, 0, 0]} />
                <Bar dataKey="absent" fill="#ef4444" name="Absent" radius={[4, 4, 0, 0]} />
                {isStaffReport && <Bar dataKey="late" fill="#f59e0b" name="Late" radius={[4, 4, 0, 0]} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart - Overall Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-purple-600" />
            Attendance Distribution
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={currentPieData || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {(currentPieData || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Daily Trend Line Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-600" />
          Last 7 Days Trend
          <span className="ml-2 text-xs font-normal text-slate-500 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Sun className="w-3 h-3" /> Sunday = Holiday
          </span>
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={currentTrend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                formatter={(value, name, props) => {
                  if (props.payload.isSunday) return ['Holiday', name];
                  return [value, name];
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} name="Present" dot={{ r: 4 }} />
              <Line type="monotone" dataKey="absent" stroke="#ef4444" strokeWidth={2} name="Absent" dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Report Table - Staff or Student */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className={`bg-gradient-to-r ${isStaffReport ? 'from-amber-50 to-orange-50' : 'from-purple-50 to-blue-50'} px-5 py-4 border-b border-slate-200`}>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            {isStaffReport ? <AlertTriangle className="w-5 h-5 text-amber-600" /> : <GraduationCap className="w-5 h-5 text-purple-600" />}
            {isStaffReport ? 'Staff Attendance Report (This Month)' : 'Student Attendance Report (This Month)'}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {isStaffReport ? 'Staff arriving after 9:15 AM are marked as late' : 'Student-wise attendance summary'}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">{isStaffReport ? 'Staff Name' : 'Student Name'}</th>
                {!isStaffReport && <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Course</th>}
                <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600">Total Days</th>
                <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600">Present</th>
                {isStaffReport && <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600">Late Arrivals</th>}
                <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600">Absent</th>
                {isStaffReport && <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600">Leave</th>}
                {!isStaffReport && <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600">Holiday</th>}
                <th className="text-center px-4 py-3 text-sm font-semibold text-slate-600">Attendance %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(currentSummary || []).map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-medium text-slate-900">{item.name}</span>
                  </td>
                  {!isStaffReport && <td className="px-4 py-3 text-sm text-slate-600">{item.course || 'N/A'}</td>}
                  <td className="px-4 py-3 text-center text-slate-600">{item.total || 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                      {item.present || 0}
                    </span>
                  </td>
                  {isStaffReport && (
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-sm font-medium ${
                        item.late > 3 ? 'bg-red-100 text-red-700' : 
                        item.late > 0 ? 'bg-amber-100 text-amber-700' : 
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {item.late || 0}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                      {item.absent || 0}
                    </span>
                  </td>
                  {isStaffReport && (
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                        {item.leave || 0}
                      </span>
                    </td>
                  )}
                  {!isStaffReport && (
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                        {item.holiday || 0}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-sm font-bold ${
                      item.total > 0 && (item.present / item.total * 100) >= 80 ? 'bg-emerald-100 text-emerald-700' :
                      item.total > 0 && (item.present / item.total * 100) >= 60 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {item.total > 0 ? Math.round((item.present / item.total) * 100) : 0}%
                    </span>
                  </td>
                </tr>
              ))}
              {(!currentSummary || currentSummary.length === 0) && (
                <tr>
                  <td colSpan={isStaffReport ? 7 : 7} className="px-4 py-8 text-center text-slate-500">
                    No attendance data available for this month
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )};

  return (
    <div className="space-y-6" data-testid="center-attendance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attendance Management</h1>
          <p className="text-slate-500 mt-1">Students aur Staff ki daily attendance track karein</p>
        </div>
        <div className="flex gap-3">
          {activeTab !== 'analytics' && (
            <>
              <Button onClick={downloadExcel} variant="outline" className="gap-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                <Download className="w-4 h-4" />
                Download Excel
              </Button>
              <Button onClick={fetchData} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button 
                onClick={saveAttendance} 
                disabled={isSaving || isSelectedDateSunday}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2"
              >
                <ClipboardCheck className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save Attendance'}
              </Button>
            </>
          )}
          {activeTab === 'analytics' && (
            <Button onClick={fetchAnalyticsData} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh Analytics
            </Button>
          )}
        </div>
      </div>

      {/* Student / Staff / Analytics Tabs */}
      <div className="bg-white rounded-xl p-1 border border-slate-200 shadow-sm inline-flex">
        <button
          onClick={() => setActiveTab('student')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'student'
              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <GraduationCap className="w-5 h-5" />
          Student Attendance
        </button>
        <button
          onClick={() => setActiveTab('staff')}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'staff'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <UserCog className="w-5 h-5" />
          Staff Attendance
        </button>
        <button
          onClick={() => { setActiveTab('analytics'); fetchAnalyticsData(); }}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'analytics'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <BarChart3 className="w-5 h-5" />
          Analytics & Reports
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && renderAnalytics()}

      {/* Student/Staff Attendance Tabs */}
      {activeTab !== 'analytics' && (
        <>
          {/* Sunday Holiday Banner */}
          {isSelectedDateSunday && (
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300 rounded-xl p-4 flex items-center gap-3">
              <Sun className="w-8 h-8 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-800">Sunday - Weekly Holiday / साप्ताहिक छुट्टी</h3>
                <p className="text-sm text-amber-700">Is din ki attendance automatically holiday mark hai</p>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className={`bg-gradient-to-br ${activeTab === 'student' ? 'from-emerald-500 to-emerald-600' : 'from-teal-500 to-teal-600'} rounded-xl p-4 text-white shadow-lg`}>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-white/80 text-sm">Present</p>
                  <p className="text-2xl font-bold">{presentCount}</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-red-200" />
                <div>
                  <p className="text-red-100 text-sm">Absent</p>
                  <p className="text-2xl font-bold">{absentCount}</p>
                </div>
              </div>
            </div>
            {activeTab === 'staff' && (
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-white/80 text-sm">Late Arrival</p>
                    <p className="text-2xl font-bold">{lateCount}</p>
                  </div>
                </div>
              </div>
            )}
            <div className={`bg-gradient-to-br ${activeTab === 'student' ? 'from-blue-500 to-blue-600' : 'from-indigo-500 to-indigo-600'} rounded-xl p-4 text-white shadow-lg`}>
              <div className="flex items-center gap-3">
                {activeTab === 'student' ? <Users className="w-8 h-8 opacity-80" /> : <UserCog className="w-8 h-8 opacity-80" />}
                <div>
                  <p className="text-white/80 text-sm">Total {activeTab === 'student' ? 'Students' : 'Staff'}</p>
                  <p className="text-2xl font-bold">{currentData.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-purple-200" />
                <div>
                  <p className="text-purple-100 text-sm">Attendance %</p>
                  <p className="text-2xl font-bold">{isSelectedDateSunday ? 'Holiday' : `${attendancePercent}%`}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Date Mode Selector */}
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <span className="text-sm font-medium text-slate-600">View Mode:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setDateMode('daily')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    dateMode === 'daily'
                      ? 'bg-purple-100 text-purple-700 border border-purple-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Daily
                </button>
                <button
                  onClick={() => setDateMode('monthly')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    dateMode === 'monthly'
                      ? 'bg-purple-100 text-purple-700 border border-purple-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <CalendarDays className="w-4 h-4" />
                  Monthly
                </button>
                <button
                  onClick={() => setDateMode('manual')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    dateMode === 'manual'
                      ? 'bg-purple-100 text-purple-700 border border-purple-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <CalendarRange className="w-4 h-4" />
                  Manual Date
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {/* Date Selector based on mode */}
              {dateMode === 'daily' && (
                <div className="flex items-center gap-2">
                  <button onClick={() => changeDate(-1)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ChevronLeft className="w-5 h-5 text-slate-600" />
                  </button>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isSelectedDateSunday ? 'bg-amber-100 border border-amber-300' : 'bg-slate-100'}`}>
                    <Calendar className={`w-4 h-4 ${isSelectedDateSunday ? 'text-amber-600' : 'text-slate-500'}`} />
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className={`bg-transparent border-none text-sm font-medium focus:outline-none ${isSelectedDateSunday ? 'text-amber-700' : 'text-slate-900'}`}
                    />
                    {isSelectedDateSunday && <Sun className="w-4 h-4 text-amber-600" />}
                  </div>
                  <button onClick={() => changeDate(1)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ChevronRight className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              )}

              {dateMode === 'monthly' && (
                <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                  <CalendarDays className="w-4 h-4 text-slate-500" />
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="bg-transparent border-none text-sm font-medium text-slate-900 focus:outline-none"
                  />
                </div>
              )}

              {dateMode === 'manual' && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                    <span className="text-xs text-slate-500">From:</span>
                    <input
                      type="date"
                      value={manualStartDate}
                      onChange={(e) => setManualStartDate(e.target.value)}
                      className="bg-transparent border-none text-sm font-medium text-slate-900 focus:outline-none"
                    />
                  </div>
                  <span className="text-slate-400">→</span>
                  <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
                    <span className="text-xs text-slate-500">To:</span>
                    <input
                      type="date"
                      value={manualEndDate}
                      onChange={(e) => setManualEndDate(e.target.value)}
                      className="bg-transparent border-none text-sm font-medium text-slate-900 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Batch Filter - Only for Students */}
              {activeTab === 'student' && (
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                >
                  <option value="all">All Batches</option>
                  {batches.map(batch => (
                    <option key={batch.id} value={batch.name || batch.batch_name}>{batch.name || batch.batch_name}</option>
                  ))}
                </select>
              )}

              <Button 
                onClick={() => markAllPresent(activeTab === 'staff')} 
                variant="outline" 
                className="gap-2 ml-auto"
                disabled={isSelectedDateSunday}
              >
                <Check className="w-4 h-4" />
                Mark All Present
              </Button>
            </div>
          </div>

          {/* Attendance Grid */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full">
              <thead className={`bg-gradient-to-r ${activeTab === 'student' ? 'from-purple-50 to-blue-50' : 'from-emerald-50 to-teal-50'} border-b border-slate-200`}>
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">#</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">{activeTab === 'student' ? 'Student' : 'Staff'}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">{activeTab === 'student' ? 'Batch/Course' : 'Role'}</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">{activeTab === 'student' ? 'Phone' : 'Department'}</th>
                  {activeTab === 'staff' && (
                    <>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">
                        <div className="flex items-center justify-center gap-1">
                          <LogIn className="w-4 h-4" /> Time In
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">
                        <div className="flex items-center justify-center gap-1">
                          <LogOut className="w-4 h-4" /> Time Out
                        </div>
                      </th>
                    </>
                  )}
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currentData.map((item, idx) => {
                  // Check if late (after 9:15 AM)
                  const isLate = activeTab === 'staff' && staffAttendance[item.id] === 'present' && staffTimeIn[item.id] && 
                    (parseInt(staffTimeIn[item.id].split(':')[0]) > 9 || 
                     (parseInt(staffTimeIn[item.id].split(':')[0]) === 9 && parseInt(staffTimeIn[item.id].split(':')[1]) > 15));

                  return (
                    <motion.tr 
                      key={item.id} 
                      className={`hover:bg-slate-50 transition-colors ${
                        isSelectedDateSunday ? 'bg-amber-50/50' :
                        currentAttendance[item.id] === 'absent' ? 'bg-red-50' : ''
                      }`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            activeTab === 'student' 
                              ? 'bg-gradient-to-br from-purple-500 to-blue-500' 
                              : 'bg-gradient-to-br from-emerald-500 to-teal-500'
                          }`}>
                            <span className="text-white font-semibold">{item.name?.charAt(0)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-slate-900">{item.name}</span>
                            {activeTab === 'staff' && <p className="text-xs text-slate-400">{item.phone}</p>}
                            {isLate && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Late</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{activeTab === 'student' ? (item.batch || item.course_name || 'N/A') : item.role || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{activeTab === 'student' ? (item.phone || item.mobile || 'N/A') : item.department || 'N/A'}</td>
                      
                      {/* Time In/Out for Staff */}
                      {activeTab === 'staff' && (
                        <>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="time"
                              value={staffTimeIn[item.id] || '09:00'}
                              onChange={(e) => setStaffTimeIn(prev => ({ ...prev, [item.id]: e.target.value }))}
                              className={`px-2 py-1 border rounded-lg text-sm text-center w-24 ${
                                isLate ? 'border-amber-400 bg-amber-50' : 'border-slate-200'
                              }`}
                              disabled={staffAttendance[item.id] === 'absent' || staffAttendance[item.id] === 'holiday' || isSelectedDateSunday}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="time"
                              value={staffTimeOut[item.id] || '18:00'}
                              onChange={(e) => setStaffTimeOut(prev => ({ ...prev, [item.id]: e.target.value }))}
                              className="px-2 py-1 border border-slate-200 rounded-lg text-sm text-center w-24"
                              disabled={staffAttendance[item.id] === 'absent' || staffAttendance[item.id] === 'holiday' || isSelectedDateSunday}
                            />
                          </td>
                        </>
                      )}
                      
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                          isSelectedDateSunday || currentAttendance[item.id] === 'holiday'
                            ? 'bg-amber-100 text-amber-700'
                            : currentAttendance[item.id] === 'present' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {isSelectedDateSunday || currentAttendance[item.id] === 'holiday' ? (
                            <><Sun className="w-3 h-3" /> Holiday</>
                          ) : currentAttendance[item.id] === 'present' ? (
                            <><CheckCircle className="w-3 h-3" /> Present</>
                          ) : (
                            <><X className="w-3 h-3" /> Absent</>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleAttendance(item.id, activeTab === 'staff')}
                          disabled={isSelectedDateSunday}
                          className={`p-2 rounded-lg transition-all ${
                            isSelectedDateSunday
                              ? 'bg-amber-100 text-amber-600 cursor-not-allowed'
                              : currentAttendance[item.id] === 'present'
                              ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                              : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }`}
                        >
                          {isSelectedDateSunday ? (
                            <Sun className="w-5 h-5" />
                          ) : currentAttendance[item.id] === 'present' ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <X className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {currentData.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              {activeTab === 'student' ? <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" /> : <UserCog className="w-16 h-16 text-slate-200 mx-auto mb-4" />}
              <p className="text-slate-500">Koi {activeTab === 'student' ? 'student' : 'staff'} nahi mila</p>
              <p className="text-sm text-slate-400 mt-2">
                {activeTab === 'staff' ? 'Staff Management me pehle staff add karein' : 'Students page me pehle students add karein'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CenterAttendancePage;
